import api from '../../api/client';

// --- Interfaces (aligned with backend schemas) ---

export interface FinBudgetLine {
  id: string;
  budget_id: string;
  account_code: string;
  account_name: string;
  category: string;
  amount_budgeted: number;
  amount_previous_year: number | null;
  amount_actual_previous: number | null;
  department: string | null;
  notes: string | null;
  sort_order: number;
}

export interface FinBudget {
  id: string;
  organization_id: string | null;
  fiscal_year: number;
  name: string;
  status: 'draft' | 'submitted' | 'review' | 'approved' | 'closed';
  total_charges: number | null;
  total_revenues: number | null;
  balance: number | null;
  steuerfuss_proposed: number | null;
  approved_by_user_id: string | null;
  approved_at: string | null;
  metadata: Record<string, unknown>;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinMultiYearPlanYear {
  year: number;
  projected_revenues: number;
  projected_charges: number;
  projected_balance: number;
  projected_debt: number;
  projected_investments: number;
}

export interface FinMultiYearPlan {
  id: string;
  organization_id: string | null;
  name: string;
  scenario_label: string | null;
  start_year: number;
  end_year: number;
  yearly_data: FinMultiYearPlanYear[];
  assumptions: Record<string, unknown>;
  status: string;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinTaxModel {
  id: string;
  organization_id: string | null;
  name: string;
  base_year: number;
  projection_years: number;
  population_growth_rate: number | null;
  steuerfuss: number | null;
  income_tax_per_capita: number | null;
  wealth_tax_per_capita: number | null;
  corporate_tax_per_employee: number | null;
  results: {
    yearly_projections: FinTaxYearlyProjection[];
    total_projected_revenue: number;
    assumptions: Record<string, unknown>;
  } | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinTaxYearlyProjection {
  year: number;
  population: number;
  employees: number;
  income_tax: number;
  wealth_tax: number;
  corporate_tax: number;
  total_revenue: number;
}

export interface FinInvestment {
  id: string;
  organization_id: string | null;
  name: string;
  description: string | null;
  category: string;
  total_cost: number | null;
  funding_sources: Record<string, unknown>;
  timeline_start: number | null;
  timeline_end: number | null;
  priority: number;
  status: 'planned' | 'approved' | 'in_progress' | 'completed';
  related_project_id: string | null;
  related_permit_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinHRM2Row {
  account_code: string;
  label: string;
  group: string;
  subgroup: string | null;
  budget_amount: number;
  actual_amount: number;
  variance: number;
  variance_pct: number;
}

export interface FinHRM2Report {
  fiscal_year: number;
  generated_at: string;
  functional_view: FinHRM2Row[];
  institutional_view: FinHRM2Row[];
  totals: {
    total_revenues: number;
    total_charges: number;
    balance: number;
  };
}

// --- Budget API ---

export async function listBudgets(params?: { fiscal_year?: number; status?: string; skip?: number; limit?: number }) {
  const res = await api.get<FinBudget[]>('/finance/budgets', { params });
  return res.data;
}

export async function getBudget(id: string) {
  const res = await api.get<FinBudget>(`/finance/budgets/${id}`);
  return res.data;
}

export async function createBudget(data: { fiscal_year: number; name: string; notes?: string }) {
  const res = await api.post<FinBudget>('/finance/budgets', data);
  return res.data;
}

export async function updateBudget(id: string, data: Partial<FinBudget>) {
  const res = await api.put<FinBudget>(`/finance/budgets/${id}`, data);
  return res.data;
}

export async function submitBudget(id: string) {
  const res = await api.post<FinBudget>(`/finance/budgets/${id}/submit`);
  return res.data;
}

export async function approveBudget(id: string) {
  const res = await api.post<FinBudget>(`/finance/budgets/${id}/approve`);
  return res.data;
}

export async function getBudgetLines(budgetId: string) {
  const res = await api.get<FinBudgetLine[]>(`/finance/budgets/${budgetId}/lines`);
  return res.data;
}

export async function addBudgetLine(budgetId: string, data: {
  account_code: string;
  account_name: string;
  category: string;
  amount_budgeted: number;
  notes?: string;
}) {
  const res = await api.post<FinBudgetLine>(`/finance/budgets/${budgetId}/lines`, data);
  return res.data;
}

// --- Multi-Year Plan API ---

export async function listPlans(params?: { skip?: number; limit?: number }) {
  const res = await api.get<FinMultiYearPlan[]>('/finance/plans', { params });
  return res.data;
}

export async function getPlan(id: string) {
  const res = await api.get<FinMultiYearPlan>(`/finance/plans/${id}`);
  return res.data;
}

export async function createPlan(data: { name: string; scenario: string; start_year: number; end_year: number; assumptions?: Record<string, unknown> }) {
  const res = await api.post<FinMultiYearPlan>('/finance/plans', {
    name: data.name,
    scenario: data.scenario,
    start_year: data.start_year,
    end_year: data.end_year,
    assumptions: data.assumptions,
  });
  return res.data;
}

export async function updatePlan(id: string, data: Partial<FinMultiYearPlan>) {
  const res = await api.put<FinMultiYearPlan>(`/finance/plans/${id}`, data);
  return res.data;
}

export async function transitionPlan(id: string, targetStatus: string) {
  const res = await api.post<FinMultiYearPlan>(`/finance/plans/${id}/transition`, { target_status: targetStatus });
  return res.data;
}

// --- Tax Model API ---

export async function listTaxModels(params?: { skip?: number; limit?: number }) {
  const res = await api.get<FinTaxModel[]>('/finance/tax-models', { params });
  return res.data;
}

export async function createTaxModel(data: {
  name: string;
  base_year: number;
  projection_years: number;
  population_growth_rate?: number;
  steuerfuss?: number;
  income_tax_per_capita?: number;
  wealth_tax_per_capita?: number;
  corporate_tax_per_employee?: number;
}) {
  const res = await api.post<FinTaxModel>('/finance/tax-models', data);
  return res.data;
}

export async function simulateTaxModel(id: string, population?: number, employees?: number) {
  const params: Record<string, number> = {};
  if (population) params.population = population;
  if (employees) params.employees = employees;
  const res = await api.post<FinTaxModel>(`/finance/tax-models/${id}/simulate`, null, { params });
  return res.data;
}

// --- Investment API ---

export async function listInvestments(params?: { status?: string; category?: string; skip?: number; limit?: number }) {
  const res = await api.get<FinInvestment[]>('/finance/investments', { params });
  return res.data;
}

export async function getInvestment(id: string) {
  const res = await api.get<FinInvestment>(`/finance/investments/${id}`);
  return res.data;
}

export async function createInvestment(data: {
  name: string;
  description?: string;
  category: string;
  total_cost?: number;
  timeline_start?: number;
  timeline_end?: number;
  priority?: number;
  status?: string;
  funding_sources?: Record<string, unknown>;
}) {
  const res = await api.post<FinInvestment>('/finance/investments', data);
  return res.data;
}

export async function updateInvestment(id: string, data: Partial<FinInvestment>) {
  const res = await api.put<FinInvestment>(`/finance/investments/${id}`, data);
  return res.data;
}

// --- HRM2 Report API ---

export async function getHRM2Report(fiscalYear: number) {
  const res = await api.get<FinHRM2Report>('/finance/hrm2/report', { params: { fiscal_year: fiscalYear } });
  return res.data;
}
